import { marker } from '@jsverse/transloco-keys-manager/marker';
import { DialogPlugin } from '@modules/forms/models/dialog-plugin';
import { MODULE_CONTEXT, ModuleId } from '@pixacare/forms';
import { WoundtrackModuleComponentByModuleId } from '../models/woundtrack-module-component-by-module-id';
import { WoundtrackSupportedModuleIds } from '../models/woundtrack-supported-module-ids';
import { PictureProviderComponent } from '../modules/picture-provider/components/picture-provider/picture-provider.component';
import { WoundsizeComponent } from '../modules/woundsize/components/woundsize/woundsize.component';
import { WoundtrackInputsComponent } from '../modules/woundtrack-inputs/components/woundtrack-inputs.component';
import { WoundtrackQuickInputsComponent } from '../modules/woundtrack-quick-inputs/components/woundtrack-quick-inputs/woundtrack-quick-inputs.component';
import { WoundtrackQuickComponent } from '../modules/woundtrack-quick/components/woundtrack-quick/woundtrack-quick.component';
import { WoundtrackComponent } from '../modules/woundtrack/components/woundtrack/woundtrack.component';

type WoundtrackModules = {
  [M in WoundtrackSupportedModuleIds]: DialogPlugin<WoundtrackModuleComponentByModuleId[M]>;
};

export const woundtrackModules = {
  [ModuleId.WOUNDTRACK_INPUTS]: {
    id: ModuleId.WOUNDTRACK_INPUTS,
    component: WoundtrackInputsComponent,
    dependencies: [],
    inputs: {
      files: `${MODULE_CONTEXT}.files`,
      medias: `${MODULE_CONTEXT}.medias`,
      sequenceId: `${MODULE_CONTEXT}.sequenceId`,
    },
    config: {
      label: marker('Détourer une lésion'),
      size: 's',
    },
    outputs: {
      pictures: 'pictures',
    },
  },
  [ModuleId.PICTURE_PROVIDER]: {
    id: ModuleId.PICTURE_PROVIDER,
    component: PictureProviderComponent,
    dependencies: [ModuleId.WOUNDTRACK_INPUTS],
    config: {
      label: marker('Détourer une lésion'),
      size: 'l',
    },
    inputs: {
      woundtrackInputs: `${ModuleId.WOUNDTRACK_INPUTS}.pictures`,
    },
    outputs: {
      picture: 'picture',
      isNewPicture: `isNewPicture`,
    },
  },
  [ModuleId.WOUNDSIZE]: {
    id: ModuleId.WOUNDSIZE,
    component: WoundsizeComponent,
    dependencies: [ModuleId.WOUNDTRACK_INPUTS, ModuleId.PICTURE_PROVIDER],
    inputs: {
      targetModuleId: `${MODULE_CONTEXT}.targetModuleId`,
      picture: `${ModuleId.PICTURE_PROVIDER}.picture`,
      isNewPicture: `${ModuleId.PICTURE_PROVIDER}.isNewPicture`,
    },
    outputs: {
      length: 'length',
      width: 'width',
      size: 'size',
      pictures: 'pictures',
      vertices: 'vertices',
    },
  },
  [ModuleId.WOUNDSIZE_LXL]: {
    id: ModuleId.WOUNDSIZE_LXL,
    component: WoundsizeComponent,
    fallback: ModuleId.WOUNDSIZE,
    dependencies: [ModuleId.WOUNDTRACK_INPUTS, ModuleId.PICTURE_PROVIDER],
    inputs: {
      targetModuleId: `${MODULE_CONTEXT}.targetModuleId`,
      picture: `${ModuleId.PICTURE_PROVIDER}.picture`,
      isNewPicture: `${ModuleId.PICTURE_PROVIDER}.isNewPicture`,
    },
    outputs: {
      length: 'length',
      width: 'width',
      size: 'size',
      pictures: 'pictures',
      vertices: 'vertices',
    },
  },
  [ModuleId.WOUNDTRACK_II]: {
    id: ModuleId.WOUNDTRACK_II,
    component: WoundtrackComponent,
    fallback: ModuleId.WOUNDSIZE_LXL,
    dependencies: [ModuleId.WOUNDTRACK_INPUTS, ModuleId.PICTURE_PROVIDER],
    inputs: {
      targetModuleId: `${MODULE_CONTEXT}.targetModuleId`,
      picture: `${ModuleId.PICTURE_PROVIDER}.picture`,
      isNewPicture: `${ModuleId.PICTURE_PROVIDER}.isNewPicture`,
    },
    outputs: {
      length: 'length',
      width: 'width',
      size: 'size',
      vertices: 'vertices',
      fibrin: 'fibrin',
      granulation: 'granulation',
      necrosis: 'necrosis',
      pictures: 'pictures',
      predictedLength: 'predictedLength',
      predictedWidth: 'predictedWidth',
      predictedSize: 'predictedSize',
      predictedVertices: 'predictedVertices',
      manualLength: 'manualLength',
      manualWidth: 'manualWidth',
      manualSize: 'manualSize',
      manualVertices: 'manualVertices',
      isManuallyMeasured: 'isManuallyMeasured',
    },
  },
  [ModuleId.WOUNDTRACK_II_QUICK_INPUTS]: {
    id: ModuleId.WOUNDTRACK_II_QUICK_INPUTS,
    component: WoundtrackQuickInputsComponent,
    dependencies: [],
    config: {
      label: marker('Analyser une lésion'),
      size: 's',
    },
    inputs: {
      medias: `${MODULE_CONTEXT}.medias`,
    },
    outputs: {
      picture: `picture`,
      isNewPicture: `isNewPicture`,
    },
  },
  [ModuleId.WOUNDTRACK_II_QUICK_STUDIO]: {
    id: ModuleId.WOUNDTRACK_II_QUICK_STUDIO,
    component: WoundtrackComponent,
    fallback: ModuleId.WOUNDSIZE_LXL,
    dependencies: [ModuleId.WOUNDTRACK_II_QUICK_INPUTS],
    inputs: {
      targetModuleId: `${MODULE_CONTEXT}.targetModuleId`,
      picture: `${ModuleId.WOUNDTRACK_II_QUICK_INPUTS}.picture`,
      isNewPicture: `${ModuleId.WOUNDTRACK_II_QUICK_INPUTS}.isNewPicture`,
    },
    outputs: {
      length: 'length',
      width: 'width',
      size: 'size',
      vertices: 'vertices',
      fibrin: 'fibrin',
      granulation: 'granulation',
      necrosis: 'necrosis',
      pictures: 'pictures',
      predictedLength: 'predictedLength',
      predictedWidth: 'predictedWidth',
      predictedSize: 'predictedSize',
      predictedVertices: 'predictedVertices',
      manualLength: 'manualLength',
      manualWidth: 'manualWidth',
      manualSize: 'manualSize',
      manualVertices: 'manualVertices',
      isManuallyMeasured: 'isManuallyMeasured',
    },
  },
  [ModuleId.WOUNDTRACK_II_QUICK]: {
    id: ModuleId.WOUNDTRACK_II_QUICK,
    component: WoundtrackQuickComponent,
    dependencies: [ModuleId.WOUNDTRACK_II_QUICK_STUDIO],
    config: {
      label: marker('Sélectionner une lésion'),
      size: 'l',
    },
    inputs: {
      quickModule: `${MODULE_CONTEXT}.quickModule`,
      medias: `${MODULE_CONTEXT}.medias`,
      length: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.length`,
      width: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.width`,
      size: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.size`,
      vertices: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.vertices`,
      fibrin: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.fibrin`,
      granulation: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.granulation`,
      necrosis: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.necrosis`,
      pictures: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.pictures`,
      predictedLength: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.predictedLength`,
      predictedWidth: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.predictedWidth`,
      predictedSize: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.predictedSize`,
      predictedVertices: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.predictedVertices`,
      manualLength: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.manualLength`,
      manualWidth: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.manualWidth`,
      manualSize: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.manualSize`,
      manualVertices: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.manualVertices`,
      isManuallyMeasured: `${ModuleId.WOUNDTRACK_II_QUICK_STUDIO}.isManuallyMeasured`,
    },
    outputs: {},
  },
} satisfies WoundtrackModules;
